import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, Info, X } from 'lucide-react';
import { usePreferences } from './PreferencesContext';

type ToastType = 'success' | 'error' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

interface ToastContextValue {
  success: (message: string, action?: ToastAction) => void;
  error: (message: string, action?: ToastAction) => void;
  info: (message: string, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_ICONS = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
};

const TOAST_BORDER_COLORS = {
  success: 'border-l-success',
  error: 'border-l-danger',
  info: 'border-l-accent',
};

const TOAST_ICON_COLORS = {
  success: 'text-success',
  error: 'text-danger',
  info: 'text-accent',
};

function ToastItem({ toast, onDismiss, duration }: { toast: Toast; onDismiss: (id: string) => void; duration: number }) {
  const [visible, setVisible] = useState(false);
  const [hovering, setHovering] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    // Slide in
    requestAnimationFrame(() => setVisible(true));
  }, []);

  useEffect(() => {
    if (hovering) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    const ms = toast.action ? Math.max(duration, 5000) : duration;
    timerRef.current = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss(toast.id), 200);
    }, ms);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [hovering, toast.id, toast.action, onDismiss, duration]);

  const Icon = TOAST_ICONS[toast.type];

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={`flex items-start gap-3 px-4 py-3 bg-bg-surface border border-border border-l-4 ${TOAST_BORDER_COLORS[toast.type]} rounded-lg shadow-xl max-w-sm transition-all duration-200 group ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
      }`}
    >
      <Icon size={18} className={`flex-shrink-0 mt-0.5 ${TOAST_ICON_COLORS[toast.type]}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-text-primary">{toast.message}</p>
        {toast.action && (
          <button
            onClick={() => { toast.action!.onClick(); onDismiss(toast.id); }}
            className="text-xs font-medium text-accent hover:text-accent-hover underline mt-1"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={() => {
          setVisible(false);
          setTimeout(() => onDismiss(toast.id), 200);
        }}
        className="flex-shrink-0 p-0.5 text-text-muted hover:text-text-primary opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X size={14} />
      </button>
    </div>
  );
}

const POSITION_CLASSES: Record<string, string> = {
  'bottom-right': 'fixed bottom-4 right-4',
  'top-right': 'fixed top-16 right-4',
  'bottom-center': 'fixed bottom-4 left-1/2 -translate-x-1/2',
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { prefs } = usePreferences();
  const [toasts, setToasts] = useState<Toast[]>([]);

  const duration = parseInt(prefs['toast.duration'] || '3000', 10) || 3000;
  const position = prefs['toast.position'] || 'bottom-right';
  const positionClass = POSITION_CLASSES[position] || POSITION_CLASSES['bottom-right'];

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((type: ToastType, message: string, action?: ToastAction) => {
    const id = crypto.randomUUID();
    setToasts(prev => [...prev, { id, message, type, action }]);
  }, []);

  const value: ToastContextValue = {
    success: useCallback((msg: string, action?: ToastAction) => addToast('success', msg, action), [addToast]),
    error: useCallback((msg: string, action?: ToastAction) => addToast('error', msg, action), [addToast]),
    info: useCallback((msg: string, action?: ToastAction) => addToast('info', msg, action), [addToast]),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={`${positionClass} z-[100] flex flex-col gap-2`}>
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} duration={duration} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
